import { yupResolver } from '@hookform/resolvers/yup';
import {
  ProductListModel,
  ProductListMutationInput,
} from 'api-hooks/product/list';
import formSetErrors from 'common/helpers/form-setError';
import notification from 'components/common/notification';
import { Form, FormState } from 'components/elements';
import FormHeader from 'components/widgets/form-header';
import useDestroyAction from 'hooks/use-destroy-action';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import { FormContent, SeparatorVertical } from 'legacy-modules/styles';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useForm } from 'react-hook-form';

import { ProductFormSchema } from './form-type';
import InformationField from './information-field';
import ProductAdjustmentDetailTable from './tables/product-adjustment-detail-table';
import ProductOrderDetailTable from './tables/product-order-detail-table';
import ProductSalesDetailTable from './tables/product-sales-detail-table';
import ProductVariantTable from './tables/product-variant-table';
import useGetDefaultValues from '../hooks/use-get-default-values';

interface Props {
  product?: ProductListModel;
  onSubmit: (
    input: ProductListMutationInput,
    form: ReturnType<typeof useForm>,
  ) => Promise<ProductListModel | undefined>;
  renderError?: React.ReactNode;
}

export default function ProductListForm(props: Props) {
  const { product } = props;
  const { t } = useTranslation();
  const { asPath } = useRouter();

  const defaultValues = useGetDefaultValues(props.product);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(ProductFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values) => {
      try {
        await props.onSubmit(values, methods as any);
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        notification.error({ message: e.message });
      }
    },
    [methods, props],
  );
  const permission = useGetCrudPermission('products');
  const { onDelete, isLoading: deleteLoading } = useDestroyAction(
    'products',
    product?.id,
  );

  const customViewName = product && `${t('common:product')} - ${product.code}`;
  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      defaultEditable={!product}
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      customViewName={customViewName}
      keyName={asPath}
    >
      <FormContent>
        <FormState>
          {({ editable }) => {
            return (
              <>
                <FormHeader
                  data={product}
                  onDelete={onDelete}
                  noDelete={!permission.delete && !editable}
                  title={
                    product
                      ? customViewName ?? t('common:product')
                      : t('common:add_extra', { extra: t('common:product') })
                  }
                  isDeleteLoading={deleteLoading}
                  noEdit={!permission.update}
                />
              </>
            );
          }}
        </FormState>
        {props.renderError}
        <div>
          <InformationField />
          <SeparatorVertical $margin={24} />
          <ProductVariantTable />
          <SeparatorVertical $margin={36} />
          <FormState>
            {({ editable }) =>
              !editable &&
              product && (
                <>
                  <ProductOrderDetailTable id={product.id} />
                  <SeparatorVertical $margin={36} />
                  <ProductSalesDetailTable id={product.id} />
                  <SeparatorVertical $margin={36} />
                  <ProductAdjustmentDetailTable id={product.id} />
                  <SeparatorVertical $margin={64} />
                </>
              )
            }
          </FormState>
        </div>
      </FormContent>
    </Form>
  );
}
